import { useCompanyStore } from "@/stores/company";
import { useEmployeeStore } from "@/stores/employees";

/**
 * Encode a single path segment (company slug, employee id).
 * @param {string} segment
 */
export function enc(segment) {
  return encodeURIComponent(segment);
}

export function dashboardPath(companySlug) {
  return `/dashboard/${enc(companySlug)}`;
}

export function accountingPath(companySlug) {
  return `/accounting/${enc(companySlug)}`;
}

export function taxationPath(companySlug) {
  return `/taxation/${enc(companySlug)}`;
}

/** @returns {{ name: string, params?: object, path?: string }} */
export function dashboardRoute() {
  const slug = useCompanyStore().selected?.slug;
  if (slug) return { name: "Dashboard", params: { companySlug: slug } };
  return { name: "DashboardEntry" };
}

export function accountingRoute() {
  const slug = useCompanyStore().selected?.slug;
  if (slug) return { name: "Accounting", params: { companySlug: slug } };
  return { name: "AccountingEntry" };
}

export function taxationRoute() {
  const slug = useCompanyStore().selected?.slug;
  if (slug) return { name: "Taxation", params: { companySlug: slug } };
  return { name: "TaxationEntry" };
}

/**
 * Build an HR URL path for a given company + employee + sub-page.
 * Sub-page omitted → resolves to the employee index which redirects
 * to profile or onboarding based on the employee's fiscogest code.
 * Employee omitted → resolves to the company index which picks an
 * employee automatically.
 * @param {string} companySlug
 * @param {string} [employeeId]
 * @param {'profile'|'salaries'|'documents'|'onboarding'} [subpage]
 */
export function hrPath(companySlug, employeeId, subpage) {
  if (!companySlug) return "/hr";
  let path = `/hr/${enc(companySlug)}`;
  if (employeeId) path += `/${enc(employeeId)}`;
  if (subpage) path += `/${subpage}`;
  return path;
}

/**
 * Generic HR route object for a given sub-page, falling back gracefully
 * when slug/id aren't known yet.
 * @param {string} subpageRouteName
 * @param {{ companySlug?: string, employeeId?: string }} [overrides]
 * @returns {{ name: string, params?: object }}
 */
function buildHrRoute(subpageRouteName, overrides = {}) {
  const slug = overrides.companySlug ?? useCompanyStore().selected?.slug;
  const employeeId = overrides.employeeId ?? useEmployeeStore().lastSelected?.id;
  if (slug && employeeId) {
    return { name: subpageRouteName, params: { companySlug: slug, employeeId } };
  }
  if (slug) {
    return { name: "HrCompanyIndex", params: { companySlug: slug } };
  }
  return { name: "HrEntry" };
}

/** @returns {{ name: string, params?: object }} */
export function hrProfileRoute(overrides) {
  return buildHrRoute("HRProfile", overrides);
}

/** @returns {{ name: string, params?: object }} */
export function hrSalariesRoute(overrides) {
  return buildHrRoute("HRSalaries", overrides);
}

/** @returns {{ name: string, params?: object }} */
export function hrDocumentsRoute(overrides) {
  return buildHrRoute("HRDocuments", overrides);
}

/** @returns {{ name: string, params?: object }} */
export function hrOnboardingRoute(overrides) {
  return buildHrRoute("HROnboarding", overrides);
}
